import { useDispatch, useSelector } from "react-redux";

import { ROUTES } from "../configs/routes";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/api";
import { RCODES } from "../constants/rcodes";
import {
  myDataHistoryDefaults,
  myDataHistoryReqBodyDefaults,
} from "../data/defaults";
import Http from "../services/http";
import {
  actionSetFilePasswordSend,
  actionSetGlobalSiteLinkPopupClose,
  actionSetHasMoreHistory,
  actionSetHistoryMoreLoading,
  actionSetHistoryReqBody,
  actionSetIsSummaryLoaded,
  actionSetIsUserEmailNotSet,
  actionSetLatestRequestHistory,
  actionSetMyDataHistory,
  actionSetMyDataHistoryLoading,
  actionSetMyDataLoadMoreHistory,
  actionSetRequestConfirmationDetails,
  actionSetRequestDetails,
  actionSetRequestDetailsLoading,
  actionSetRequestLoading,
  actionSetSummary,
  actionSetSummaryLoading,
  confirmRequestData,
  actionSetShowServiceToastError,
} from "../store/actions/mydata-actions";
import { BaseEntityRes } from "../types/base";
import {
  HISTORY_REQUEST_TYPE,
  MyDataHistoryEntity,
  MyDataHistoryReqBody,
  SORT_TYPE,
} from "../types/history";
import { RequestEntityReq, SummaryEntityRes } from "../types/mydata";
import { RequestDetailsEntityRes } from "../types/request-details";
import { REQUEST_TYPE, USER_TYPE } from "../types/user";
import { UtilsDateTime } from "../utils/UtilsDateTIme";
import { UtilsLocale } from "../utils/UtilsLocale";
import { UtilsRequest } from "../utils/UtilsRequest";

import Cookies from "js-cookie";
import {
  EVENT_ACTION_DETAILS_SEND_FILE_PASSWORD,
  EVENT_CATEGORY_MY_DATA,
  EVENT_CATEGORY_MY_DATA_HISTORY,
  EVENT_CATEGORY_REQUEST_DETAILS,
  EVENT_MY_DATA_ACTION_REQUEST_CONFIRMATION,
  EVENT_MY_DATA_HISTORY_ACTION_ALL,
  EVENT_MY_DATA_HISTORY_ACTION_DELETION,
  EVENT_MY_DATA_HISTORY_ACTION_DOWNLOAD,
  EVENT_MY_DATA_HISTORY_ACTION_OPTOUT,
} from "../constants/analytics";
import { REQUEST_STATUS } from "../constants/status";
import { LANGUAGE_TRANS, USER_DATA_NAMESPACE } from "../constants/storage";
import { LocalStorage } from "../services/storage";
import { ApplicationState } from "../store";
import { LayoutState } from "../store/reducers/layout-reducer";
import { UserState } from "../store/reducers/user-reducer";
import GoogleAnalytics from "../utils/GoogleAnalytics";
import { UtilsHome } from "../utils/UtilsHome";
import { getParsedLocalStorage } from "../utils/UtilsParseLocalStorage";
import { useUserHooks } from "./user-hooks";

export const useMyDataHooks = () => {
  const dispatch = useDispatch();
  const { userData } = useUserHooks();
  // state
  const isUserSAorDL =
    userData &&
    (userData.userType === USER_TYPE.SA || userData.userType === USER_TYPE.DL);

  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  // pass geoIP for email login for sa_cc
  // since profile API sa_cc doesn't update during SPAP override on GEOIP
  const saCC = isUserSAorDL
    ? userState.userData.countryCode
    : userState.userCountryCode;

  const loadSummary = async () => {
    try {
      dispatch(actionSetSummaryLoading());

      const url = `${API_BASE_URL}${API_ENDPOINTS.SUMMARY}`;
      const res: SummaryEntityRes = await Http.get(url, {
        lang: layoutState.languageTrans,
        sa_cc: UtilsLocale.convertISO3toISO2(saCC),
        site_cc: layoutState.countryCode,
      });

      // throw new Error("This is a custom error message.");
      dispatch(actionSetSummary(res));
    } catch (e: any) {
      console.error(e);
      dispatch(actionSetIsSummaryLoaded(false));
    }
  };
  const redirectToSAConfirmPassword = (url: string) => {
    globalThis.location.href = url;
  };
  const confirmRequest = async (
    services: Array<string>,
    requestType: REQUEST_TYPE,
    confirmToken: string
  ) => {
    try {
      dispatch(actionSetRequestLoading());
      const tz = UtilsDateTime.getLocalTimezone();
      const isDownloadOrDeleteRequest =
        requestType === REQUEST_TYPE.DOWNLOAD ||
        requestType === REQUEST_TYPE.DELETE;
      const isUserSAorDL =
        userData &&
        (userData.userType === USER_TYPE.SA ||
          userData.userType === USER_TYPE.DL);

      const arrServices: { service_code: string }[] = [];
      let url;
      let language = Cookies.get(LANGUAGE_TRANS) || layoutState.languageTrans;
      const lsUserData = JSON.parse(
        LocalStorage.get(USER_DATA_NAMESPACE) || ""
      );

      services.forEach((service) => {
        arrServices.push({ service_code: service });
      });

      if (
        lsUserData?.countryCode === "USA" &&
        layoutState.countryCode === "US" &&
        language === "en"
      ) {
        language = "en-rUS";
      }

      if (requestType === REQUEST_TYPE.DOWNLOAD) {
        url = `${API_BASE_URL}${API_ENDPOINTS.MYDATA_DOWNLOAD}`;
      } else if (requestType === REQUEST_TYPE.DELETE) {
        url = `${API_BASE_URL}${API_ENDPOINTS.MYDATA_DELETE}`;
      } else {
        url = `${API_BASE_URL}${API_ENDPOINTS.MYDATA_UNSUBSCRIBE}`;
      }

      const data: RequestEntityReq = {
        user_request_date: UtilsDateTime.dateObjectToStringUTC(new Date()),
        request_subtype: "R",
        language,
        country: layoutState.countryCode,
        sa_country: UtilsLocale.convertISO3toISO2(lsUserData?.countryCode),
        service_count: arrServices.length,
        service_list: arrServices,
        tz,
        geoip_country: userState.userCountryCode,
      };
      const extraHeaders = {
        "x-sc-confirm-token": confirmToken,
      };
      let res;
      if (requestType === REQUEST_TYPE.UNSUBSCRIBE) {
        res = await Http.post(url, data, extraHeaders);
      } else {
        res = await Http.post(url, data);
      }

      switch (res.rcode) {
        case RCODES.SUCCESS:
          GoogleAnalytics.triggerEvent(
            EVENT_CATEGORY_MY_DATA,
            EVENT_MY_DATA_ACTION_REQUEST_CONFIRMATION[requestType]
          );

          if (isDownloadOrDeleteRequest && isUserSAorDL) {
            redirectToSAConfirmPassword(res.confirmPasswordUrl);
          } else {
            // allow normal flow with gk duplicate request
            const { rcode, rmsg, ...newResponse } = res;
            dispatch(actionSetLatestRequestHistory(newResponse));
            dispatch(confirmRequestData(res));
            resetServiceRequestErrorToast();
          }
          break;
        case RCODES.SA_UNVERIFIED_ERROR:
          dispatch(
            actionSetShowServiceToastError({
              isError: true,
              hasRendered: true,
              isSAUnverifiedError: true,
            })
          );
          break;
        default:
          dispatch(
            actionSetShowServiceToastError({
              isError: true,
              hasRendered: true,
              isSAUnverifiedError: false,
            })
          );
          break;
      }

      UtilsRequest.removeRequestInfo();
    } catch (e: any) {
      console.error(e);
      switch (e.rcode) {
        case RCODES.SA_UNVERIFIED_ERROR:
          dispatch(
            actionSetShowServiceToastError({
              isError: true,
              hasRendered: true,
              isSAUnverifiedError: true,
            })
          );
          break;
        default:
          dispatch(
            actionSetShowServiceToastError({
              isError: true,
              hasRendered: true,
              isSAUnverifiedError: false,
            })
          );
          break;
      }
    }
  };

  const setRequestConfirmationDetails = (requestNumber: string) => {
    dispatch(actionSetRequestConfirmationDetails(requestNumber));
  };

  const clearConfirmRequestData = () => {
    dispatch(confirmRequestData({ confirmPasswordUrl: "", requestNumber: "" }));
  };

  // TODO: Move to separate hook file for history hooks function
  const getUserRequestCount = async (): Promise<number> => {
    const lsUserData = getParsedLocalStorage(USER_DATA_NAMESPACE, {});
    try {
      const url = `${API_BASE_URL}${API_ENDPOINTS.HISTORY}`;
      const res: MyDataHistoryEntity = await Http.post(url, {
        request_type: HISTORY_REQUEST_TYPE.ALL,
        sort_by: SORT_TYPE.date_desc,
        limit: 10,
        offset: 0,
        lang: layoutState.languageTrans,
        sa_cc: UtilsLocale.convertISO3toISO2(lsUserData?.countryCode),
        site_cc: layoutState.countryCode,
      });

      return res.request_count;
    } catch (e) {
      console.error("failed to fetch request count", e);
      return 0;
    }
  };

  const handleUserRedirectWhenInvalidRequest = async (rcode: number) => {
    if (rcode !== RCODES.INVALID_REQUEST_NUMBER) return;

    const requestCount = await getUserRequestCount();
    const currentURL = globalThis.location.pathname;

    if (requestCount <= 0) {
      globalThis.location.href = ROUTES.ROOT;
      return;
    }

    if (UtilsHome.hasLocalSite(userState.userCountryCode)) {
      window.location.href = ROUTES.MYDATA_HISTORY;
      return;
    }

    globalThis.location.href = currentURL.includes(ROUTES.MYDATA_HISTORY)
      ? ROUTES.MYDATA_HISTORY
      : ROUTES.MYDATA;
  };

  const loadRequestDetails = async (request_number: string) => {
    const lsUserData = getParsedLocalStorage(USER_DATA_NAMESPACE, {});
    try {
      dispatch(actionSetRequestDetailsLoading());
      const url = `${API_BASE_URL}${API_ENDPOINTS.DETAILS}`;
      const res: RequestDetailsEntityRes = await Http.post(
        url,
        {},
        {
          request_number,
          lang: layoutState.languageTrans,
          sa_cc: UtilsLocale.convertISO3toISO2(lsUserData?.countryCode),
          site_cc: layoutState.countryCode,
        }
      );

      dispatch(actionSetRequestDetails(res));
      return res;
    } catch (e: any) {
      handleUserRedirectWhenInvalidRequest(e.rcode);
      const errorMessage =
        e && typeof e === "object" && "rmsg" in e ? e.rmsg : undefined;
      console.error(e);
      const emptyRequestDetails: RequestDetailsEntityRes = {
        rcode: e.rcode || RCODES.INTERNAL_SERVER_ERROR,
        rmsg: errorMessage,
        request_number: "",
        request_type: "",
        request_status: "",
        lang_trns: "",
        sub_request_count: 0,
        sub_request_list: [],
        user_request_date: "",
        display_survey: false,
      };
      return emptyRequestDetails;
    }
  };

  const sendFilePassword = async (request_number: string) => {
    try {
      GoogleAnalytics.triggerEvent(
        EVENT_CATEGORY_REQUEST_DETAILS,
        EVENT_ACTION_DETAILS_SEND_FILE_PASSWORD
      );
      const url = `${API_BASE_URL}${API_ENDPOINTS.SEND_FILE_PASSWORD}`;
      const res: BaseEntityRes = await Http.post(
        url,
        {},
        {
          request_number,
        }
      );

      if (res.rcode === 0) {
        dispatch(actionSetFilePasswordSend(true));
      }
    } catch (e: any) {
      console.error(e);
      switch (e.rcode) {
        case RCODES.FILE_PASSWORD_NOT_SENT:
          dispatch(actionSetFilePasswordSend(false));
          break;
        case RCODES.USER_EMAIL_NOT_SET:
          dispatch(actionSetIsUserEmailNotSet(true));
          break;
        default:
          dispatch(actionSetFilePasswordSend(false));
      }
    }
  };

  const closeFilePasswordModal = () => {
    dispatch(actionSetFilePasswordSend(false));
    dispatch(actionSetIsUserEmailNotSet(false));
  };

  const handleGAEvent = (request_type: string, request_status?: string) => {
    const requestStatus = request_status ? request_status : "L";
    switch (request_type) {
      case HISTORY_REQUEST_TYPE.DOWNLOAD:
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_DOWNLOAD[requestStatus]
        );
        break;
      case HISTORY_REQUEST_TYPE.DELETE:
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_DELETION[requestStatus]
        );
        break;
      case HISTORY_REQUEST_TYPE.UNSUBSCRIBE:
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_OPTOUT[requestStatus]
        );
        break;
      case HISTORY_REQUEST_TYPE.ALL:
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_MY_DATA_HISTORY,
          EVENT_MY_DATA_HISTORY_ACTION_ALL[requestStatus]
        );
        break;
    }
  };

  const loadMyDataHistory = async (
    body: MyDataHistoryReqBody,
    isNextPage: boolean = false
  ) => {
    try {
      dispatch(actionSetHasMoreHistory(false));
      dispatch(actionSetMyDataHistoryLoading());
      dispatch(actionSetHistoryMoreLoading(true));

      if (
        body.request_status &&
        (body.request_status === REQUEST_STATUS.REQUESTING ||
          body.request_status === REQUEST_STATUS.QUEUED)
      ) {
        body.request_status = REQUEST_STATUS.PROCESSING;
      }
      handleGAEvent(body.request_type, body.request_status);
      if (!body.request_status) {
        delete body.request_status;
      }

      const lsUserData = JSON.parse(
        LocalStorage.get(USER_DATA_NAMESPACE) || ""
      );
      const saCountryCode = saCC || lsUserData?.countryCode;
      if (!isNextPage) {
        dispatch(actionSetMyDataHistory(myDataHistoryDefaults));
        body = {
          ...body,
          offset: 0,
          lang: layoutState.languageTrans,
          sa_cc: UtilsLocale.convertISO3toISO2(saCountryCode),
          site_cc: layoutState.countryCode,
        };
      }

      dispatch(actionSetHistoryReqBody(body));
      const url = `${API_BASE_URL}${API_ENDPOINTS.HISTORY}`;
      const res: MyDataHistoryEntity = await Http.post(url, body);

      if (isNextPage) {
        dispatch(actionSetMyDataLoadMoreHistory(res));
      } else {
        dispatch(actionSetMyDataHistory(res));
      }

      dispatch(actionSetHistoryMoreLoading(false));

      if (res.next_offset && res.next_offset > 0) {
        dispatch(actionSetHasMoreHistory(true));
      } else {
        dispatch(actionSetHasMoreHistory(false));
      }
    } catch (e: any) {
      console.error(e);
    }
  };

  const closeGlobalSiteLinkPopup = () => {
    dispatch(actionSetGlobalSiteLinkPopupClose());
  };

  const resetRequestBody = () => {
    dispatch(actionSetHistoryReqBody(myDataHistoryReqBodyDefaults));
  };

  const resetServiceRequestErrorToast = () => {
    dispatch(
      actionSetShowServiceToastError({
        isError: false,
        hasRendered: false,
        isSAUnverifiedError: false,
      })
    );
  };

  return {
    loadSummary,
    loadRequestDetails,
    loadMyDataHistory,
    sendFilePassword,
    closeFilePasswordModal,
    confirmRequest,
    clearConfirmRequestData,
    closeGlobalSiteLinkPopup,
    resetRequestBody,
    resetServiceRequestErrorToast,
    setRequestConfirmationDetails,
  };
};
